// Component: Toast.jsx
// Purpose: DS toast/snackbar system. <ToastProvider> renders a portal viewport
//   and manages the queue; useToast() returns { show, success, error, info,
//   warning, dismiss }. Auto-dismiss (hover-to-pause), manual close, badge-colored
//   variants, DS-motion slide/fade. Replaces the scattered MUI Snackbar usage.

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { cx } from "./cx";

const ToastContext = createContext(null);

let seq = 0; //! module-level id source (avoids Math.random / collisions)

//--> Small inline glyphs per variant (16px, currentColor).
const ICONS = {
  success: (
    <path d="M20 6 9 17l-5-5" />
  ),
  error: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v4M12 16h.01" />
    </>
  ),
  warning: (
    <>
      <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
      <path d="M12 9v4M12 17h.01" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 16v-4M12 8h.01" />
    </>
  ),
};

//--> Coerce ANY toast content to a safe renderable. A non-string payload (e.g. an axios error
//*   body `{status, error}` handed to a toast by a global error handler) rendered as a React
//*   child throws "Objects are not valid as a React child" — and with no error boundary above
//*   <ToastProvider> that unmounts the whole app (white screen). A toast must NEVER crash the tree:
//*   pass strings/elements through untouched; dig a human message out of a known error shape; else
//*   stringify. (Root-caused from a deploy 422 `{status:"error", error:{code,message}}` white-screen.)
function toRenderable(v) {
  if (v == null || typeof v === "string") return v;
  if (React.isValidElement(v)) return v;
  if (typeof v !== "object") return String(v);
  const msg =
    v.message ||
    (v.error && v.error.message) ||
    (v.detail && v.detail.error && v.detail.error.message) ||
    (v.detail && typeof v.detail === "string" ? v.detail : null) ||
    (v.detail && v.detail.message);
  if (typeof msg === "string" && msg) return msg;
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

function ToastIcon({ variant }) {
  return (
    <span className="ds-toast-icon" aria-hidden="true">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        {ICONS[variant] || ICONS.info}
      </svg>
    </span>
  );
}

function ToastItem({ toast, onDismiss }) {
  const { id, variant, title, message, duration, leaving } = toast;
  const timerRef = useRef(null);
  const remainingRef = useRef(duration);
  const startRef = useRef(0);

  const clear = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const resume = useCallback(() => {
    if (duration <= 0 || leaving) return;
    startRef.current = Date.now();
    timerRef.current = setTimeout(() => onDismiss(id), remainingRef.current);
  }, [duration, leaving, id, onDismiss]);

  const pause = useCallback(() => {
    if (!timerRef.current) return;
    clear();
    remainingRef.current -= Date.now() - startRef.current;
  }, [clear]);

  useEffect(() => {
    resume();
    return clear;
  }, [resume, clear]);

  return (
    <div
      className={cx("ds-toast", variant, leaving && "leaving")}
      role="status"
      aria-live={variant === "error" ? "assertive" : "polite"}
      onMouseEnter={pause}
      onMouseLeave={resume}
    >
      <ToastIcon variant={variant} />
      <div className="ds-toast-body">
        {title != null && <div className="ds-toast-title">{toRenderable(title)}</div>}
        {message != null && <div className="ds-toast-msg">{toRenderable(message)}</div>}
      </div>
      <button type="button" className="ds-toast-close" aria-label="Dismiss" onClick={() => onDismiss(id)}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 6 6 18M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

export function ToastProvider({ children, position = "bottom-right" }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    //--> Mark leaving so the exit animation plays, then remove.
    setToasts((ts) => ts.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
    setTimeout(() => setToasts((ts) => ts.filter((t) => t.id !== id)), 200);
  }, []);

  const show = useCallback((opts) => {
    const base = typeof opts === "string" ? { message: opts } : opts || {};
    const id = ++seq;
    setToasts((ts) => [...ts, { id, variant: "info", duration: 4000, ...base }]);
    return id;
  }, []);

  const api = useMemo(
    () => ({
      show,
      dismiss,
      success: (message, opts) => show({ ...opts, message, variant: "success" }),
      error: (message, opts) => show({ ...opts, message, variant: "error" }),
      info: (message, opts) => show({ ...opts, message, variant: "info" }),
      warning: (message, opts) => show({ ...opts, message, variant: "warning" }),
    }),
    [show, dismiss]
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div className={cx("ds-toast-viewport", position)}>
          {toasts.map((t) => (
            <ToastItem key={t.id} toast={t} onDismiss={dismiss} />
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within a <ToastProvider>");
  return ctx;
}

export default ToastProvider;
